// src/components/widgets/Navigation/Navigation.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { notificationService } from '../../services/notificationService';
import './Navigation.scss';

// SVG иконки
const Icons = {
    conf: () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />
        </svg>
    ),
    devices: () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5" y="2" width="14" height="20" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
            <line x1="9" y1="5" x2="15" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="18" r="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
    ),
    profile: () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" fill="none" />
            <path
                d="M4 20c0-4 4-6 8-6s8 2 8 6"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
            />
        </svg>
    ),
    support: () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7V9zm8 0h2v2h-2V9zm-4 0h2v2h-2V9z"
                fill="currentColor"
            />
        </svg>
    ),
};

interface NavItem {
    id: string;
    path: string;
    icon: keyof typeof Icons;
    label: string;
}

const navItems: NavItem[] = [
    { id: 'conf', path: '/', icon: 'conf', label: 'Главная' },
    { id: 'devices', path: '/devices', icon: 'devices', label: 'Устройства' },
    { id: 'profile', path: '/profile', icon: 'profile', label: 'Профиль' },
    { id: 'support', path: '/support', icon: 'support', label: 'Поддержка' },
];

// Компонент бейджа с уведомлениями
const NotificationBadge: React.FC<{ count: number }> = ({ count }) => {
    if (count <= 0) return null;

    const displayCount = count > 99 ? '99+' : count;

    return <span className="notification-badge">{displayCount}</span>;
};

// Десктопная боковая навигация
const DesktopNav: React.FC<{ unreadCount: number }> = ({ unreadCount }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeIndex, setActiveIndex] = useState(0);
    const navListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const currentIndex = navItems.findIndex((item) => item.path === location.pathname);
        setActiveIndex(currentIndex);
    }, [location.pathname]);

    useEffect(() => {
        if (navListRef.current) {
            navListRef.current.style.setProperty('--active-index', String(activeIndex));
        }
    }, [activeIndex]);

    return (
        <nav className="side-nav">
            <div className="side-nav-header">
                <div className="side-nav-logo">
                    <span>
                        Shadow
                        <br />
                        Net
                    </span>
                </div>
            </div>

            <div className="side-nav-center">
                <div className={`side-nav-items ${activeIndex === -1 ? 'no-active' : ''}`} ref={navListRef}>
                    <div className="side-nav-indicator-wrap">
                        <div className="side-nav-indicator-bg" />
                    </div>

                    {navItems.map((item) => {
                        const IconComponent = Icons[item.icon];
                        const isActive = location.pathname === item.path;
                        const isSupport = item.id === 'support';

                        return (
                            <button
                                key={item.id}
                                className={`side-nav-item ${isActive ? 'active' : ''}`}
                                onClick={() => navigate(item.path)}
                                type="button"
                            >
                                <span className="side-nav-icon">
                                    <IconComponent />
                                    {isSupport && <NotificationBadge count={unreadCount} />}
                                </span>
                                <span className="side-nav-label">{item.label}</span>
                                <span className="side-nav-dot" />
                            </button>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
};

// Мобильная навигация с анимацией
const MobileNav: React.FC<{ unreadCount: number }> = ({ unreadCount }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeIndex, setActiveIndex] = useState(0);
    const navRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const currentIndex = navItems.findIndex((item) => item.path === location.pathname);
        setActiveIndex(currentIndex);
    }, [location.pathname]);

    useEffect(() => {
        if (navRef.current) {
            navRef.current.style.setProperty('--active-index', activeIndex.toString());
        }
    }, [activeIndex]);

    return (
        <nav className={`bottom-nav ${activeIndex === -1 ? 'no-active' : ''}`} ref={navRef}>
            <div className="nav-indicator-wrap">
                <div className="nav-indicator" />
            </div>

            {navItems.map((item) => {
                const IconComponent = Icons[item.icon];
                const isActive = location.pathname === item.path;
                const isSupport = item.id === 'support';

                return (
                    <button
                        key={item.id}
                        className={`nav-item ${isActive ? 'active' : ''}`}
                        onClick={() => navigate(item.path)}
                        type="button"
                    >
                        <span className="nav-icon">
                            <IconComponent />
                            {isSupport && <NotificationBadge count={unreadCount} />}
                        </span>
                    </button>
                );
            })}
        </nav>
    );
};

function Navigation() {
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const [unreadCount, setUnreadCount] = useState(0);
    const telegramId = useAppStore((state) => state.telegramId);
    const isAuthenticated = useAppStore((state) => state.isAuthenticated);

    // Подписка на уведомления
    useEffect(() => {
        if (isAuthenticated && telegramId) {
            notificationService.init(telegramId);

            const unsubscribe = notificationService.subscribe((count: number) => {
                setUnreadCount(count);
            });

            return () => {
                unsubscribe();
            };
        }

        notificationService.clear();
        setUnreadCount(0);
        return undefined;
    }, [telegramId, isAuthenticated]);

    // Отслеживание изменения размера окна
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (isDesktop) {
        return <DesktopNav unreadCount={unreadCount} />;
    }

    return <MobileNav unreadCount={unreadCount} />;
}

export default Navigation;